import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { AutocompleteExchange } from 'src/Common/components';
import { Exchange } from 'src/Common/types/exchange';

interface Props {
  open: boolean;
  onClose: () => void;
  onRestart: (exchanges: string[]) => Promise<void>;
  isLoading: boolean;
  disabled: boolean;
}

export const RestartConnectorsDialog = ({
  open,
  onClose,
  onRestart,
  isLoading,
  disabled
}: Props) => {
  const [selectedExchanges, setSelectedExchanges] = useState<Exchange[]>([]);

  const handleRestart = async () => {
    if (selectedExchanges.length === 0) return;
    await onRestart(selectedExchanges.map(e => e.id));
    setSelectedExchanges([]);
    onClose();
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedExchanges([]);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '1rem',
          opacity: 0.7,
          borderBottom: '0.1px solid #393939'
        }}
      >
        Restart Connectors
      </DialogTitle>

      <DialogContent sx={{ pt: '1.5rem !important', pb: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Select the exchanges to restart. Leave empty to restart all.
          </Typography>
          <AutocompleteExchange
            multiple
            disableCloseOnSelect
            value={selectedExchanges}
            onChange={(_, value) => {
              setSelectedExchanges(
                Array.isArray(value) ? (value as Exchange[]) : []
              );
            }}
            inputLabel="Exchanges"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: '1rem 1.5rem', borderTop: '0.1px solid #393939' }}>
        <Button onClick={handleClose} disabled={isLoading} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRestart}
          disabled={disabled || isLoading}
          size="small"
        >
          {isLoading ? (
            <CircularProgress size={18} />
          ) : selectedExchanges.length === 0 ? (
            'Restart All'
          ) : (
            `Restart ${selectedExchanges.length} connector${selectedExchanges.length > 1 ? 's' : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
